import { useCallback, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { AlertTriangle, Clock3, FilePlus2, RefreshCw, Repeat } from 'lucide-react';
import { formatCurrency } from '@/lib/i18n';
import { VENUE_EVENTS } from '@/lib/shared-types';
import { useApi, rowsOf } from '@/Hooks/useApi';
import { usePermission } from '@/Hooks/usePermission';
import { useRealtime, useRealtimeResync } from '@/Hooks/useRealtime';
import { useAppSelector } from '@/Redux/store';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Field, Select } from '@/components/ui/field';
import { Icon } from '@/components/ui/icon';
import { Input } from '@/components/ui/input';
import { Table, TableWrap, THead, TH, TR, TD, TableEmpty } from '@/components/ui/table';
import PanelResult from './PanelResult';
import DocumentDetailDialog from './DocumentDetailDialog';
import BuildDocumentDialog from './BuildDocumentDialog';
import {
  FISCAL_ROUTES,
  FISCAL_PERMISSIONS,
  FiscalDocumentStatus,
  FiscalEnvironment,
  type FiscalReconciliation,
  type FiscalDocumentRow,
} from '../fiscalApi';
import { documentStatusTone, formatAge, formatDateTime } from '../lib';

const STATUS_FILTERS: readonly FiscalDocumentStatus[] = Object.values(FiscalDocumentStatus);

/**
 * THE PRODUCT (`docs/fiscal.md`): pending, accepted, rejected, retrying — with
 * a rejected document presented as an operator TASK, never as a failed sale.
 *
 * `GET /fiscal/reconciliation` is one call answering all four counts plus the
 * single most useful number on the screen — how long the oldest unreported
 * sale has waited — and the three problems behind however many rejections
 * there are, grouped on the STABLE error code rather than a free-text authority
 * message that changes wording between releases.
 */
export default function ReconciliationPanel() {
  const { t, i18n } = useTranslation();
  const { can } = usePermission();
  const activeBranchId = useAppSelector((state) => state.ui.activeBranchId);

  const [branchScope, setBranchScope] = useState<'active' | 'all'>('active');
  const [environment, setEnvironment] = useState<FiscalEnvironment | ''>('');
  const [status, setStatus] = useState<FiscalDocumentStatus | ''>('');
  const [search, setSearch] = useState('');
  const [openDocumentId, setOpenDocumentId] = useState<string | null>(null);
  const [buildOpen, setBuildOpen] = useState(false);

  const branchId = branchScope === 'active' ? (activeBranchId ?? undefined) : undefined;

  const summaryParams = useMemo(
    () => ({
      ...(branchId ? { branchId } : {}),
      ...(environment ? { environment } : {}),
    }),
    [branchId, environment],
  );

  const summary = useApi<FiscalReconciliation>(
    can(FISCAL_PERMISSIONS.view) ? FISCAL_ROUTES.reconciliation : null,
    summaryParams,
  );

  const listParams = useMemo(
    () => ({
      ...(branchId ? { branchId } : {}),
      ...(environment ? { environment } : {}),
      ...(status ? { status } : {}),
      ...(search.trim() ? { search: search.trim() } : {}),
      // No `sortBy`: the service's own default is `issuedAt desc`, which is
      // already "worst/newest first" — no reason to name a client-chosen
      // column and risk sending one the API has not allow-listed.
      limit: 100,
    }),
    [branchId, environment, status, search],
  );

  const documents = useApi<unknown>(
    can(FISCAL_PERMISSIONS.view) ? FISCAL_ROUTES.documents : null,
    listParams,
  );
  const { rows } = rowsOf<FiscalDocumentRow>(documents.data);

  const reload = useCallback(() => {
    summary.reload();
    documents.reload();
  }, [summary, documents]);

  // Every submission, retry, cancel, adjust and abandon writes a
  // `fiscal_document.updated` event from inside the transaction that changes
  // the row, so the screen an operator has open refetches the instant a
  // document moves rather than waiting for a manual refresh.
  useRealtime(VENUE_EVENTS.FISCAL_DOCUMENT_UPDATED, reload, branchId ? [`branch:${branchId}`] : undefined);
  useRealtimeResync(reload);

  const clearFilters = () => {
    setEnvironment('');
    setStatus('');
    setSearch('');
  };
  const filtered = Boolean(environment || status || search.trim());

  const money = (amount: string, currency: string) => formatCurrency(amount, currency, i18n.language);

  return (
    <div className="space-y-5">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="flex flex-wrap items-center gap-2">
          <Button
            variant={branchScope === 'active' ? 'default' : 'outline'}
            size="sm"
            onClick={() => setBranchScope('active')}
            disabled={!activeBranchId}
          >
            {t('fiscal.reconciliation.thisBranch')}
          </Button>
          <Button
            variant={branchScope === 'all' ? 'default' : 'outline'}
            size="sm"
            onClick={() => setBranchScope('all')}
          >
            {t('fiscal.reconciliation.allBranches')}
          </Button>
        </div>
        <div className="flex items-center gap-2">
          <Button variant="outline" size="sm" onClick={reload}>
            <Icon as={RefreshCw} />
            {t('common.refresh')}
          </Button>
          {can(FISCAL_PERMISSIONS.retry) && (
            <Button size="sm" onClick={() => setBuildOpen(true)}>
              <Icon as={FilePlus2} />
              {t('fiscal.reconciliation.buildForCheck')}
            </Button>
          )}
        </div>
      </div>

      {/* ---- the four numbers, and the two that matter most ---- */}
      {summary.error ? (
        <div
          role="alert"
          className="rounded-xl border border-red-300 bg-red-50/70 p-4 text-sm dark:border-red-900 dark:bg-red-950/30"
        >
          <p className="font-medium text-red-700 dark:text-red-300">
            {t('fiscal.states.summaryFailed')}
          </p>
          <p className="mt-1 text-ink-muted">{summary.error}</p>
          <p className="mt-1 text-xs text-ink-muted">{t('fiscal.states.loadFailedHint')}</p>
        </div>
      ) : (
        <div className="grid grid-cols-2 gap-4 lg:grid-cols-4">
          <StatCard
            label={t('fiscal.reconciliation.pending')}
            value={summary.data?.pending}
            tone="warning"
          />
          <StatCard
            label={t('fiscal.reconciliation.accepted')}
            value={summary.data?.accepted}
            tone="success"
          />
          <StatCard
            label={t('fiscal.reconciliation.rejected')}
            value={summary.data?.rejected}
            tone="danger"
            emphasize={Boolean(summary.data?.rejected)}
          />
          <StatCard
            label={t('fiscal.reconciliation.retrying')}
            value={summary.data?.retrying}
            hint={t('fiscal.reconciliation.retryingHint')}
            tone="info"
          />
        </div>
      )}

      {summary.data?.oldestPending && (
        <div className="flex items-start gap-3 rounded-xl border border-amber-300 bg-amber-50/70 p-4 text-sm dark:border-amber-900 dark:bg-amber-950/30">
          <Icon as={Clock3} className="mt-0.5 shrink-0 text-amber-600 dark:text-amber-400" />
          <div className="min-w-0 flex-1">
            <p className="font-medium">
              {t('fiscal.reconciliation.oldestPending', {
                number: summary.data.oldestPending.number,
                age: formatAge(summary.data.oldestPending.issuedAt, i18n.language),
              })}
            </p>
            <p className="text-xs text-ink-muted">
              {formatDateTime(summary.data.oldestPending.issuedAt, i18n.language)} ·{' '}
              {t('fiscal.reconciliation.attempts', { count: summary.data.oldestPending.attempts })}
            </p>
          </div>
          <Button variant="outline" size="sm" onClick={() => setOpenDocumentId(summary.data!.oldestPending!.id)}>
            {t('common.view')}
          </Button>
        </div>
      )}

      {!!summary.data?.rejectionsByCode.length && (
        <div className="rounded-xl border p-4">
          <p className="mb-2 flex items-center gap-2 text-sm font-medium">
            <Icon as={AlertTriangle} className="text-red-600 dark:text-red-400" />
            {t('fiscal.reconciliation.rejectionsByCode')}
          </p>
          <div className="flex flex-wrap gap-2">
            {summary.data.rejectionsByCode.map((row) => (
              <Badge key={row.code} tone="danger" className="font-mono">
                {row.code} · {row.count}
              </Badge>
            ))}
          </div>
        </div>
      )}

      {/* ---- the document list ---- */}
      <div className="flex flex-wrap items-end gap-3">
        <Field label={t('fiscal.reconciliation.filterStatus')} controlId="fiscal-status">
          <Select value={status} onChange={(e) => setStatus(e.target.value as FiscalDocumentStatus | '')}>
            <option value="">{t('fiscal.reconciliation.allStatuses')}</option>
            {STATUS_FILTERS.map((s) => (
              <option key={s} value={s}>
                {t(`fiscal.status.${s}`)}
              </option>
            ))}
          </Select>
        </Field>
        <Field label={t('fiscal.reconciliation.filterEnvironment')} controlId="fiscal-env">
          <Select value={environment} onChange={(e) => setEnvironment(e.target.value as FiscalEnvironment | '')}>
            <option value="">{t('fiscal.reconciliation.allEnvironments')}</option>
            <option value={FiscalEnvironment.SANDBOX}>{t('fiscal.environment.SANDBOX')}</option>
            <option value={FiscalEnvironment.PRODUCTION}>{t('fiscal.environment.PRODUCTION')}</option>
          </Select>
        </Field>
        <Field label={t('fiscal.reconciliation.search')} controlId="fiscal-search">
          <Input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder={t('fiscal.reconciliation.searchPlaceholder')}
          />
        </Field>
      </div>

      <TableWrap>
        <PanelResult
          loading={documents.loading}
          error={documents.error}
          count={rows.length}
          onRetry={documents.reload}
          subject={t('fiscal.reconciliation.subject')}
          filtered={filtered}
          onClearFilters={clearFilters}
          emptyTitle={t('fiscal.reconciliation.emptyTitle')}
          emptyHint={t('fiscal.reconciliation.emptyHint')}
        >
          <Table>
            <THead>
              <TR>
                <TH>{t('fiscal.reconciliation.columns.number')}</TH>
                <TH>{t('fiscal.reconciliation.columns.status')}</TH>
                <TH>{t('fiscal.reconciliation.columns.kind')}</TH>
                <TH>{t('fiscal.reconciliation.columns.environment')}</TH>
                <TH className="text-end">{t('fiscal.reconciliation.columns.total')}</TH>
                <TH className="text-end">{t('fiscal.reconciliation.columns.attempts')}</TH>
                <TH>{t('fiscal.reconciliation.columns.issued')}</TH>
                <TH>{t('fiscal.reconciliation.columns.lastError')}</TH>
                <TH />
              </TR>
            </THead>
            <tbody>
              {rows.length === 0 ? (
                <TableEmpty colSpan={9}>{t('fiscal.reconciliation.emptyTitle')}</TableEmpty>
              ) : (
                rows.map((doc) => (
                  <TR key={doc.id} className="cursor-pointer" onClick={() => setOpenDocumentId(doc.id)}>
                    <TD className="font-mono text-xs">{doc.number}</TD>
                    <TD>
                      <Badge tone={documentStatusTone(doc.status)}>{t(`fiscal.status.${doc.status}`)}</Badge>
                    </TD>
                    <TD>{t(`fiscal.kind.${doc.kind}`)}</TD>
                    <TD>{t(`fiscal.environment.${doc.environment}`)}</TD>
                    <TD className="text-end tabular-nums">{money(doc.totalAmount, doc.currency)}</TD>
                    <TD className="text-end tabular-nums">
                      {doc.attempts > 0 ? (
                        <span className="inline-flex items-center gap-1">
                          <Icon as={Repeat} size="sm" className="text-ink-muted" />
                          {doc.attempts}
                        </span>
                      ) : (
                        '—'
                      )}
                    </TD>
                    <TD className="text-xs text-ink-muted">{formatDateTime(doc.issuedAt, i18n.language)}</TD>
                    <TD className="max-w-xs truncate text-xs text-ink-muted" title={doc.lastErrorMessage ?? undefined}>
                      {doc.lastErrorCode ? (
                        <span className="font-mono text-red-600 dark:text-red-400">{doc.lastErrorCode}</span>
                      ) : (
                        '—'
                      )}
                    </TD>
                    <TD>
                      <Button variant="ghost" size="sm" onClick={(e) => { e.stopPropagation(); setOpenDocumentId(doc.id); }}>
                        {t('common.view')}
                      </Button>
                    </TD>
                  </TR>
                ))
              )}
            </tbody>
          </Table>
        </PanelResult>
      </TableWrap>

      <DocumentDetailDialog
        documentId={openDocumentId}
        onClose={() => setOpenDocumentId(null)}
        onChanged={reload}
      />
      <BuildDocumentDialog open={buildOpen} onClose={() => setBuildOpen(false)} onBuilt={reload} />
    </div>
  );
}

function StatCard({
  label,
  value,
  hint,
  tone,
  emphasize,
}: {
  label: string;
  value: number | undefined;
  hint?: string;
  tone: 'warning' | 'success' | 'danger' | 'info';
  emphasize?: boolean;
}) {
  const toneClass = {
    warning: 'text-amber-600 dark:text-amber-400',
    success: 'text-green-600 dark:text-green-400',
    danger: 'text-red-600 dark:text-red-400',
    info: 'text-sky-600 dark:text-sky-400',
  }[tone];

  return (
    <Card className={emphasize ? 'border-red-300 dark:border-red-900' : undefined}>
      <CardHeader>
        <CardTitle className="text-sm font-medium text-ink-muted">{label}</CardTitle>
      </CardHeader>
      <CardContent>
        <p className={`text-3xl font-semibold tabular-nums ${toneClass}`}>{value ?? '—'}</p>
        {hint && <p className="mt-1 text-xs text-ink-muted">{hint}</p>}
      </CardContent>
    </Card>
  );
}
